import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  FileText, Upload, Loader2, AlertTriangle, CheckCircle2, RefreshCw,
  History, Hash, Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SOLO ADMIN — publica el texto de cobertura que el comerciante acepta.
 *
 * El endpoint existia desde el 23-set y nunca tuvo pantalla, asi que el texto
 * solo se podia publicar con una llamada a mano. Eso dejaba a Mike sin forma de
 * cambiar su propio documento legal, y es justo lo que va a querer hacer cuando
 * su abogado devuelva la version revisada.
 *
 * Tres cosas que esta pantalla tiene que decir en voz alta, porque son las que
 * sorprenden despues:
 *
 *  - El hash se calcula sobre el texto exacto. Un espacio de mas lo cambia, y
 *    el hash es lo que ata a cada comerciante a lo que firmo.
 *  - Publicar desactiva la version anterior pero NO la borra. Las aceptaciones
 *    viejas siguen apuntando a su texto.
 *  - Si alguien tiene la pantalla de aceptacion abierta mientras se publica, su
 *    aceptacion se rechaza y tiene que recargar. Es deliberado: nadie queda
 *    atado a una version que no vio.
 */
export default function AdminCoverageTermsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [vigente, setVigente] = useState(null);
  const [sinPublicar, setSinPublicar] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);

  const [version, setVersion] = useState('');
  const [origen, setOrigen] = useState('');
  const [texto, setTexto] = useState('');
  const [publicando, setPublicando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [rV, rH] = await Promise.all([
        fetch(`${API}/insurance/terms`, { headers: auth }),
        fetch(`${API}/insurance/terms/history`, { headers: auth }),
      ]);

      // 503 no es un error: es el sistema diciendo que todavia no hay texto.
      if (rV.status === 503) {
        setVigente(null);
        setSinPublicar(true);
      } else if (rV.ok) {
        setVigente(await rV.json());
        setSinPublicar(false);
      }

      if (rH.ok) {
        const h = await rH.json();
        setHistorial(h.versions || []);
      }
    } catch (e) {
      setAviso({ tipo: 'error', texto: 'No se pudo leer el estado de los terminos.' });
    } finally {
      setCargando(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar(); }, [cargar]);

  const publicar = async () => {
    setPublicando(true);
    setAviso(null);
    try {
      const r = await fetch(`${API}/insurance/terms`, {
        method: 'POST',
        headers: auth,
        body: JSON.stringify({ version, text: texto, source: origen || null }),
      });
      const d = await r.json();
      if (!r.ok) {
        const detalle = typeof d.detail === 'string'
          ? d.detail
          : (d.detail?.message || 'No se pudo publicar.');
        setAviso({ tipo: 'error', texto: detalle });
        return;
      }
      setAviso({
        tipo: 'ok',
        texto: `Version ${d.version} publicada. Hash ${d.terms_hash?.slice(0, 16)}...`,
      });
      setVersion(''); setOrigen(''); setTexto('');
      setConfirmando(false);
      await cargar();
    } catch (e) {
      setAviso({ tipo: 'error', texto: 'La llamada fallo. Nada se publico.' });
    } finally {
      setPublicando(false);
    }
  };

  const listoParaPublicar = version.trim() && texto.trim();
  const yaExiste = historial.some((h) => h.version === version.trim());

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="admin-coverage-terms">
      <div className="flex items-start gap-3 mb-6">
        <FileText className="w-7 h-7 text-emerald-600 mt-0.5" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Coverage terms</h1>
          <p className="text-slate-500 text-sm mt-1">
            The wording merchants accept before coverage is issued. Admin only.
          </p>
        </div>
      </div>

      {aviso && (
        <div
          data-testid="terms-banner"
          className={`rounded-xl border p-4 mb-6 text-sm ${
            aviso.tipo === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:text-rose-200'
          }`}
        >
          {aviso.texto}
        </div>
      )}

      {/* ---------- Lo que esta vigente ahora ---------- */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Currently published</h2>
          <Button size="sm" variant="ghost" onClick={cargar} disabled={cargando}>
            <RefreshCw className={`w-4 h-4 mr-1 ${cargando ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {cargando && <p className="text-sm text-slate-500">Reading...</p>}

        {!cargando && sinPublicar && (
          <div
            data-testid="terms-none"
            className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-900 dark:text-amber-200">
                No terms are published.
              </p>
              <p className="text-amber-800 dark:text-amber-300 mt-1">
                Until a version is published here, no merchant can accept anything and
                no coverage can be issued against an acceptance. The acceptance screen
                says so rather than pretending otherwise.
              </p>
            </div>
          </div>
        )}

        {!cargando && vigente && (
          <dl className="grid gap-3 text-sm" data-testid="terms-active">
            <Fila etiqueta="Version">{vigente.version}</Fila>
            <Fila etiqueta="Published">
              {vigente.published_at ? new Date(vigente.published_at).toLocaleString() : '-'}
            </Fila>
            {vigente.source && <Fila etiqueta="Written by">{vigente.source}</Fila>}
            <Fila etiqueta="Hash">
              <code className="font-mono text-xs break-all">{vigente.terms_hash}</code>
            </Fila>
            <Fila etiqueta="Length">
              {(vigente.text || '').length.toLocaleString()} characters
            </Fila>
          </dl>
        )}
      </section>

      {/* ---------- Publicar una version nueva ---------- */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 mb-6">
        <h2 className="font-semibold mb-1">Publish a new version</h2>
        <p className="text-sm text-slate-500 mb-5">
          Paste the wording exactly as it should be accepted.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="text-sm">
            <span className="block mb-1 font-medium">Version</span>
            <input
              data-testid="terms-version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="2026-09-26.1"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2"
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1 font-medium">Written by <span className="text-slate-400">(optional)</span></span>
            <input
              data-testid="terms-source"
              value={origen}
              onChange={(e) => setOrigen(e.target.value)}
              placeholder="counsel, insurer, SelleSend"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2"
            />
          </label>
        </div>

        <label className="text-sm block mb-4">
          <span className="block mb-1 font-medium">Text</span>
          <textarea
            data-testid="terms-text"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={14}
            placeholder="The full wording..."
            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 font-mono text-xs"
          />
          <span className="block text-xs text-slate-400 mt-1">
            {texto.length.toLocaleString()} characters
          </span>
        </label>

        {/* Lo que sorprende despues, dicho antes */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4 text-sm space-y-2 mb-5">
          <p className="flex gap-2">
            <Hash className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              The hash is taken over this exact text. One extra space changes it, and
              the hash is what ties each merchant to what they signed.
            </span>
          </p>
          <p className="flex gap-2">
            <History className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              Publishing deactivates the current version but does not delete it.
              Earlier acceptances keep pointing at their own text.
            </span>
          </p>
          <p className="flex gap-2">
            <Users className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              Anyone with the acceptance screen open will have their acceptance
              refused and will need to reload. That is deliberate: nobody is bound to
              a version they did not see.
            </span>
          </p>
        </div>

        {yaExiste && (
          <p
            data-testid="terms-version-exists"
            className="text-sm text-amber-700 dark:text-amber-300 mb-3"
          >
            Version {version.trim()} already exists. Publishing will overwrite that
            entry rather than adding a new one.
          </p>
        )}

        {!confirmando ? (
          <Button
            data-testid="terms-publish"
            onClick={() => setConfirmando(true)}
            disabled={!listoParaPublicar}
          >
            <Upload className="w-4 h-4 mr-2" />
            Publish
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">
              Publish version {version.trim()} and make it the wording merchants accept?
            </span>
            <Button
              data-testid="terms-publish-confirm"
              onClick={publicar}
              disabled={publicando}
            >
              {publicando
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Publishing</>
                : <><CheckCircle2 className="w-4 h-4 mr-2" />Yes, publish</>}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmando(false)} disabled={publicando}>
              Cancel
            </Button>
          </div>
        )}
      </section>

      {/* ---------- Historial ---------- */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
        <h2 className="font-semibold mb-4">History</h2>
        {historial.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing published yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="terms-history">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-2 pr-4">Version</th>
                  <th className="py-2 pr-4">Published</th>
                  <th className="py-2 pr-4">Hash</th>
                  <th className="py-2 pr-4 text-right">Chars</th>
                  <th className="py-2 pr-4 text-right">Accepted by</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {historial.map((h) => (
                  <tr key={h.version} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-4 font-medium">{h.version}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {h.published_at ? new Date(h.published_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      <code className="font-mono text-xs">{(h.terms_hash || '').slice(0, 12)}...</code>
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {(h.text_length || 0).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">{h.acceptances ?? 0}</td>
                    <td className="py-2">
                      {h.active ? (
                        <span className="text-emerald-600 font-medium">Active</span>
                      ) : (
                        <span className="text-slate-400">Superseded</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-400 mt-3">
              Superseded versions are kept on purpose. Merchants who accepted one are
              still bound to that text, and it has to stay retrievable.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function Fila({ etiqueta, children }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      <dt className="text-slate-500 w-32 shrink-0">{etiqueta}</dt>
      <dd className="flex-1 min-w-0">{children}</dd>
    </div>
  );
}
